import { ExternalLink, Flag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

export interface Mention {
  id: string;
  source: 'reddit' | 'web';
  keyword_matched: string;
  title: string | null;
  snippet: string | null;
  url: string;
  author: string | null;
  subreddit: string | null;
  score: number | null;
  published_at: string | null;
  fetched_at: string;
  status: 'new' | 'flagged' | 'dismissed';
  relevance_score: number;
}

interface MentionCardProps {
  mention: Mention;
  onFlag: (id: string) => void;
  onDismiss: (id: string) => void;
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'unknown date';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function MentionCard({ mention, onFlag, onDismiss }: MentionCardProps) {
  const isReddit = mention.source === 'reddit';
  const isFlagged = mention.status === 'flagged';

  return (
    <div className={cn(
      'bg-white dark:bg-gray-900 border rounded-xl p-4 transition-colors',
      isFlagged
        ? 'border-amber-400 dark:border-amber-500 border-l-4'
        : 'border-gray-200 dark:border-gray-800',
    )}>
      {/* Header row */}
      <div className="flex items-start gap-2 flex-wrap mb-2">
        {/* Source badge */}
        <Badge className={cn(
          'text-xs font-semibold uppercase tracking-wide shrink-0',
          isReddit
            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-100'
            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-100',
        )}>
          {isReddit ? 'Reddit' : 'Web'}
        </Badge>

        {/* Subreddit chip */}
        {mention.subreddit && (
          <Badge variant="outline" className="text-xs shrink-0">
            r/{mention.subreddit}
          </Badge>
        )}

        {/* Keyword chip */}
        <Badge className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 hover:bg-purple-100 shrink-0">
          {mention.keyword_matched}
        </Badge>

        {/* Flagged indicator */}
        {isFlagged && (
          <Badge className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100 shrink-0">
            Flagged
          </Badge>
        )}
      </div>

      {/* Title */}
      {mention.title && (
        <p className="font-medium text-gray-900 dark:text-white text-sm leading-snug mb-1 line-clamp-2">
          {mention.title}
        </p>
      )}

      {/* Snippet */}
      {mention.snippet && (
        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
          {mention.snippet}
        </p>
      )}

      {/* Meta row */}
      <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 flex-wrap mb-3">
        {mention.author && <span>u/{mention.author}</span>}
        {mention.author && <span>·</span>}
        <span>{domainOf(mention.url)}</span>
        <span>·</span>
        <span>{relativeTime(mention.published_at ?? mention.fetched_at)}</span>
        {mention.score !== null && (
          <>
            <span>·</span>
            <span>{mention.score} pts</span>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-2">
        <a
          href={mention.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="outline"
            className={cn(
              'h-7 px-2 text-xs',
              isFlagged
                ? 'border-amber-400 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                : 'text-amber-600 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20',
            )}
            onClick={() => onFlag(mention.id)}
            title={isFlagged ? 'Unflag' : 'Flag for action'}
          >
            <Flag className="h-3 w-3 mr-1" />
            {isFlagged ? 'Unflag' : 'Flag'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={() => onDismiss(mention.id)}
            title="Dismiss"
          >
            <X className="h-3 w-3 mr-1" />
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}
